import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articleList } from "@/mocks/articles";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [articleMenuOpen, setArticleMenuOpen] = useState(false);
  const articleMenuRef = useRef<HTMLDivElement>(null);
  const articleBtnRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!articleMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        articleMenuRef.current &&
        !articleMenuRef.current.contains(e.target as Node) &&
        articleBtnRef.current &&
        !articleBtnRef.current.contains(e.target as Node)
      ) {
        setArticleMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [articleMenuOpen]);

  const handleArticleClick = (slug: string) => {
    setArticleMenuOpen(false);
    setMobileOpen(false);
    navigate(`/${slug}`);
    window.scrollTo(0, 0);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-600 transition-colors"
            onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
          >
            <span className="font-heading text-xl font-bold tracking-tight">
              Sugargoo<span className="text-primary-500">Sheet</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-home-line text-sm"></i>
              Home
            </Link>

            {/* Shopping Guide Dropdown */}
            <div className="relative">
              <button
                ref={articleBtnRef}
                onClick={() => setArticleMenuOpen(!articleMenuOpen)}
                className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-book-open-line text-sm"></i>
                Shopping Guides
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${articleMenuOpen ? "rotate-180" : ""}`}></i>
              </button>
              {articleMenuOpen && (
                <div
                  ref={articleMenuRef}
                  className="absolute top-full left-0 mt-2 w-72 bg-background-50 border border-background-200/80 rounded-xl z-50 py-2 max-h-80 overflow-y-auto"
                >
                  {articleList.map((article) => (
                    <button
                      key={article.slug}
                      onClick={() => handleArticleClick(article.slug)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 transition-colors text-left cursor-pointer"
                    >
                      <i className="ri-article-line text-primary-500 text-sm flex-shrink-0"></i>
                      <div className="min-w-0 text-left">
                        <span className="text-xs font-medium block truncate">{article.title}</span>
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Main Store
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            onClick={() => { setMobileOpen(!mobileOpen); setArticleMenuOpen(false); }}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden pb-5 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
              className="flex items-center gap-2 text-sm font-medium text-foreground-700 hover:text-primary-600 py-2.5 px-2 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i> Home
            </Link>

            {/* Mobile: Categories */}
            <div className="pt-2 pb-1">
              <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-2">Categories</span>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 py-2 px-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-primary-500 text-sm w-4 h-4 flex items-center justify-center`}></i>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>

            {/* Mobile: Articles */}
            <div className="pt-3 pb-1">
              <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-2">Shopping Guides</span>
            </div>
            {articleList.map((article) => (
              <button
                key={article.slug}
                onClick={() => handleArticleClick(article.slug)}
                className="flex items-center gap-2 text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 py-2 px-2 rounded-lg transition-colors cursor-pointer text-left w-full"
              >
                <i className="ri-article-line text-primary-500 text-sm flex-shrink-0"></i>
                <span className="truncate">{article.title.length > 45 ? article.title.slice(0, 45) + "..." : article.title}</span>
              </button>
            ))}

            <div className="pt-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 text-sm font-medium text-foreground-700 hover:text-primary-600 py-2.5 px-2 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <i className="ri-store-line"></i> Main Store
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 mt-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer"
              >
                <i className="ri-shopping-bag-line"></i> Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}